import {
  Spacer,
  LaunchPage,
  sortByDateTime,
  TableTagCard,
  Badge,
  MaterialUiTable,
  usePersistMuiTableState,
  MaterialUiColumnsProps,
  renderCellWithLabel,
  Status,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  selectCardsList,
  selectCardsListStatus,
  selectNewCardsList,
} from "./cardsSlice";
import { CardsListDisplayedType, CardsListType } from "./cardsAPI";
import styled from "styled-components";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { LittleCardAsset } from "@qivia/ui/src/assets/assets";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { documentDownloadXlsxAsync } from "../../../../libSlice/downloadFileXslx";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { formatCurrency } from "../../../../libSlice/materialUiFormats";
import { useSearchParams } from "react-router-dom";
import { useRef } from "react";
import "../../../../i18n/i18n";

export type AddressInput = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  country: string;
  name?: string;
};

interface SharedInterfaceCardsProps {
  sidePanelAction?: (props: Partial<CardsListType>) => void;
  sidePanel?: JSX.Element;
}

export const SharedInterfaceCards = (props: SharedInterfaceCardsProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const pageName = "cards";

  const [listDisplayed, setListDisplayed] = useState<
    CardsListDisplayedType[] | null
  >(null);

  const [searchParams] = useSearchParams();
  const fromNavigate = searchParams.get("fromNavigate") === "true";

  const [rowToSendToTop, setRowToSendToTop] = useState<string | null>(null);

  const hasExecutedFromNavigate = useRef(false);
  const shouldExecuteFromNavigate = useRef(false);

  if (fromNavigate && !shouldExecuteFromNavigate.current) {
    shouldExecuteFromNavigate.current = true;
  }

  const newCardsList = useAppSelector(selectNewCardsList);
  const cardsListStatus = useAppSelector(selectCardsListStatus);
  const cardsListInit = useAppSelector(selectCardsList);

  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      last4Digits: "size",
      status: "size",
      vehicleInfo: "size",
      ruleName: "size",
      driverName: "size",
      monthlyExpenses: "avg",
      qrCodesIdentifier: "size",
    },
  };
  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const findKeysToTranslate = useCallback(
    (key: keyof CardsListType, value: string) => {
      switch (key) {
        case "status":
          return t(`cards.status.${value}`);
        default:
          return value;
      }
    },
    [t],
  );

  const keysToTranslate = useMemo(
    () => ["status"] as (keyof CardsListDisplayedType)[],
    [],
  );

  const cardsList: CardsListType[] = useMemo(() => {
    const rowsWithId = cardsListInit.map((av) => {
      let newDict: CardsListType & { id: string } = {
        ...av,
        id: av.uuid,
      };
      Object.entries(av).forEach(([key, value]) => {
        if (keysToTranslate.includes(key as keyof CardsListDisplayedType)) {
          newDict = {
            ...newDict,
            [key]: value
              ? findKeysToTranslate(key as keyof CardsListType, value as string)
              : "",
          };
        }
      });
      return newDict;
    });
    return sortByDateTime(rowsWithId, "desc");
  }, [cardsListInit, findKeysToTranslate, keysToTranslate]);

  useEffect(() => {
    if (cardsList && cardsListStatus === "success") {
      const cardsListUpdated = cardsList.map((card) => {
        if (newCardsList.vehicles.includes(card.vehicleUuid)) {
          return { ...card, card: "(new)" };
        } else {
          return card;
        }
      });
      const cardsDateTimeSorted = sortByDateTime(cardsListUpdated, "desc");
      setListDisplayed(cardsDateTimeSorted);
    }
  }, [cardsList, cardsListStatus, dispatch, newCardsList]);

  useEffect(() => {
    if (params.id) {
      const cardSelected = cardsList.find((c) => c.uuid === params.id);
      if (cardSelected) {
        const firstPath = location.pathname.split("/")[1]; // home or departments
        navigate(`/${firstPath}/cards/cards/${cardSelected.uuid}`);
      }
    }
  }, [cardsList, location.pathname, navigate, params.id]);

  // Only after a navigate from an other page like transaction, we select the row to send to the top of the MaterialUiTable
  useEffect(() => {
    if (
      params.id &&
      listDisplayed &&
      cardsList &&
      shouldExecuteFromNavigate.current &&
      !hasExecutedFromNavigate.current
    ) {
      hasExecutedFromNavigate.current = true;
      shouldExecuteFromNavigate.current = false;

      const cardInDb = cardsList.find((card) => card.uuid === params.id);

      if (cardInDb) {
        apiRef.current.setRowSelectionModel([cardInDb.uuid]);
        setRowToSendToTop(cardInDb.uuid);
      }
    }
  }, [params.id, listDisplayed, cardsList, apiRef]);

  useEffect(() => {
    if (rowToSendToTop) {
      setRowToSendToTop(null);
    }
  }, [rowToSendToTop]);

  const searchBarProps = {
    values: cardsList,
    setFilterValues: setListDisplayed,
    keysToIgnore: [
      "uuid" as const,
      "date" as const,
      "driverUuid" as const,
      "vehicleUuid" as const,
      "ruleUuid" as const,
      "driverFirstName" as const,
      "driverLastName" as const,
      "monthlyAmountlimit" as const,
    ],
  };

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("cards.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  const columnsDisplayed: {
    [index in keyof CardsListDisplayedType]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      last4Digits: {
        type: "string",
        width: 220,
        renderCell: (p) =>
          renderCellWithLabel(
            p,
            "Éléments",
            <StyledRow>
              <LittleCardAsset />
              <Spacer x={1} />
              {(p.formattedValue as string)?.endsWith("(new)") ? (
                <Badge
                  text={t("cards.status.badge.new")}
                  backgroundColor={
                    colors["colors/system/informative/informative_ultraLight"]
                  }
                  textColor={
                    colors["colors/system/informative/informative_normal"]
                  }
                />
              ) : (
                (p.formattedValue as string) &&
                `** ${p.formattedValue as string}`
              )}
            </StyledRow>,
          ),
      },
      status: {
        type: "string",
        valueFormatter: (value: Status) =>
          value && (
            <StyledRow>
              <TableTagCard status={value} />
            </StyledRow>
          ),
      },
      vehicleInfo: {
        type: "string",
        width: 300,
      },
      vehicleAnalyticCode: {
        type: "string",
        width: 200,
      },
      ruleName: {
        type: "string",
        width: 250,
      },
      driverName: {
        type: "string",
        width: 250,
      },
      monthlyExpenses: {
        type: "number",
        valueFormatter: formatCurrency,
      },
      qrCodesIdentifier: {
        type: "string",
        width: 200,
      },
    };
  }, [t]);

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  return (
    <>
      <MaterialUiTable<keyof CardsListDisplayedType, CardsListDisplayedType>
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        onRowClickAction={props.sidePanelAction}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
        downloadViewAction={downloadViewAction}
        rowToSendToTop={rowToSendToTop}
      />
      {props.sidePanel ? props.sidePanel : <></>}
    </>
  );
};

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;
